// src/features/groups/contexts/GroupsContext.js
// Global store for the user's group list subscription (single source of truth)

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { subscribeToUserGroups } from '../services/groupService';

const GroupsContext = createContext(undefined);

const initialState = {
  items: null, // null means we have not received the first snapshot yet
  hasServerSnapshot: false,
  error: null,
};

export const GroupsProvider = ({ children }) => {
  const { uid, status } = useAuth();
  const [state, setState] = useState(() => ({ ...initialState }));
  const prevUidRef = useRef(null);
  const isSignedIn = Boolean(uid);
  const [isOffline, setIsOffline] = useState(() => {
    if (typeof navigator === 'undefined') return false;
    return navigator.onLine === false;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return;
    }

    const updateOffline = () => {
      setIsOffline(navigator.onLine === false);
    };

    window.addEventListener('online', updateOffline);
    window.addEventListener('offline', updateOffline);

    return () => {
      window.removeEventListener('online', updateOffline);
      window.removeEventListener('offline', updateOffline);
    };
  }, []);

  useEffect(() => {
    // Wait for auth to resolve before subscribing
    if (status === 'loading') {
      return;
    }

    // Signed out state: show empty immediately (no skeleton)
    if (!uid) {
      prevUidRef.current = null;
      setState({ items: [], hasServerSnapshot: false, error: null });
      return;
    }

    const uidChanged = prevUidRef.current !== uid;
    prevUidRef.current = uid;

    // For a new user, reset to unknown. For the same user, keep visible data (SWR).
    setState((prev) => (
      uidChanged
        ? { ...initialState }
        : { ...prev, error: null }
    ));

    const unsubscribe = subscribeToUserGroups(
      uid,
      (items, meta) => {
        const fromCache = meta?.fromCache === true;
        const isServerSnapshot = !fromCache;

        if (isServerSnapshot) {
          // Server is authoritative, including empty
          setState({
            items,
            hasServerSnapshot: true,
            error: null,
          });
          return;
        }

        // Cache snapshot with data: show immediately
        if (items.length > 0) {
          setState((prev) => ({
            items,
            hasServerSnapshot: prev.hasServerSnapshot,
            error: null,
          }));
          return;
        }

        // Cache says empty: keep showing any visible data; otherwise mark as checked cache.
        setState((prev) => {
          const prevItems = Array.isArray(prev.items) ? prev.items : [];
          const keepVisible = prevItems.length > 0;

          return {
            ...prev,
            items: keepVisible ? prevItems : [],
          };
        });
      },
      (err) => {
        console.error('Groups subscription error:', err);
        setState((prev) => ({
          ...prev,
          items: Array.isArray(prev.items) ? prev.items : [],
          error: 'groups.load_error',
        }));
      }
    );

    return () => {
      unsubscribe?.();
    };
  }, [uid, status]);

  const value = useMemo(() => ({ ...state, isOffline, isSignedIn }), [state, isOffline, isSignedIn]);

  return (
    <GroupsContext.Provider value={value}>
      {children}
    </GroupsContext.Provider>
  );
};

export const useGroupsContext = () => {
  const context = useContext(GroupsContext);
  if (context === undefined) {
    throw new Error('useGroupsContext must be used within a GroupsProvider');
  }
  return context;
};
